import type { BrowserOptions } from '@sentry/vue'
import { getApiBaseUrl } from '@/utils/config'

// Sentry configuration
export const sentryConfig: BrowserOptions = {
  // Replace with your actual Sentry DSN
  dsn: import.meta.env.VITE_SENTRY_DSN || '',
  
  // Environment configuration
  environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development',
  
  // Release information
  release: import.meta.env.VITE_APP_VERSION || '1.0.0',
  
  // Performance monitoring
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in production, 100% in development
  
  // Replays for debugging
  replaysSessionSampleRate: import.meta.env.PROD ? 0.01 : 0.1, // 1% in production, 10% in development
  replaysOnErrorSampleRate: 1.0, // Always capture replays on error
  
  // Privacy and filtering
  beforeSend(event, hint) {
    // Filter out development errors in production
    if (import.meta.env.PROD) {
      // Don't send console errors in production
      if (event.level === 'log') {
        return null
      }
      
      // Filter out specific error types you don't want to track
      const error = hint.originalException
      if (error instanceof Error) {
        // Example: Filter out network errors
        if (error.name === 'NetworkError' || error.message.includes('Network')) {
          return null
        }
        
        // Example: Filter out chunk loading errors (common in SPAs)
        if (error.message.includes('Loading chunk')) {
          return null
        }
      }
    }
    
    return event
  },
  
  // Integration configuration
  integrations: [
    // Replay integration will be added in main.ts
  ],
  
  // Transport options - use tunnel to bypass ad blockers
  tunnel: `${getApiBaseUrl()}/tunnel`,
  
  // Debug mode (only in development)
  debug: !import.meta.env.PROD,
  
  // Initial scope configuration
  initialScope: {
    tags: {
      component: 'vue-app',
      source: 'frontend'
    },
    user: {
      // Will be set dynamically when user logs in
    },
    contexts: {
      app: {
        name: 'SODA Registry',
        version: import.meta.env.VITE_APP_VERSION || '1.0.0'
      }
    }
  }
}

// Environment variables validation
export const validateSentryConfig = (): boolean => {
  if (!sentryConfig.dsn) {
    console.warn('Sentry DSN not configured. Error tracking will be disabled.')
    return false
  }
  
  return true
}

// Helper function to add breadcrumb
export const addSentryBreadcrumb = (message: string, category: string, level: 'info' | 'warning' | 'error' = 'info') => {
  if (typeof window !== 'undefined' && window.Sentry) {
    window.Sentry.addBreadcrumb({
      message,
      category,
      level,
      timestamp: Date.now() / 1000
    })
  }
}

// Helper function to capture exception
export const captureSentryException = (error: Error, context?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.Sentry) {
    window.Sentry.withScope((scope: any) => {
      if (context) {
        Object.keys(context).forEach(key => {
          scope.setContext(key, context[key])
        })
      }
      window.Sentry.captureException(error)
    })
  }
}